// components/KaspiConsole.js
// Консоль Kaspi: администратор шлёт НАСТОЯЩИЕ запросы интеграции (v1 - как Kaspi сейчас, v2 - новый
// контракт) и видит ответ ровно так, как его видит Kaspi: HTTP-статус, время, тело.
// Авторизация - короткоживущий пользовательский JWT для учётной записи администратора, который выдаёт
// бэкенд (POST /api/admin/kaspi-console/token): эндпоинты интеграции не принимают админский токен.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Col, Grid, Input, InputNumber, Row, Select, Space, Switch, Tag, Typography, message } from 'antd';
import { ADMIN_API_BASE, adminApi } from '../adminApi';
import './KaspiConsole.css';

const { Text } = Typography;

// Отдельный клиент без перехватчиков adminApi: 401/404/500 должны отображаться, а не выкидывать
// администратора на страницу входа.
const integrationApi = axios.create({ baseURL: ADMIN_API_BASE, validateStatus: () => true });

// Тип операции задаёт цвет карточки - чтобы с первого взгляда было видно, что именно нажимаем.
const KINDS = {
  start: { color: '#389e0d', background: '#f6ffed', label: 'Старт' },
  read: { color: '#1677ff', background: '#f0f5ff', label: 'Чтение' },
  stop: { color: '#cf1322', background: '#fff1f0', label: 'Стоп' },
  info: { color: '#595959', background: '#fafafa', label: 'Справочно' },
};

const METHOD_COLORS = { GET: 'blue', POST: 'green' };

// v1 - токен исходной авторизации (/api/kaspi). v2 - токен API v2 от имени выбранного провайдера:
// администратор может действовать от имени любого провайдера, токен всегда на одного.
function useConsoleToken(provider) {
  const [username, setUsername] = useState(null);
  const cache = useRef({});

  const getToken = useCallback(async () => {
    const key = provider || 'v1';
    const cached = cache.current[key];
    if (cached && Date.now() < cached.expiresAt - 60_000) {
      return cached.token;
    }
    const response = provider
      ? await adminApi.post('/api/admin/kaspi-console/provider-token', null, { params: { provider } })
      : await adminApi.post('/api/admin/kaspi-console/token');
    cache.current[key] = { token: response.data.accessToken, expiresAt: new Date(response.data.expiresAt).getTime() };
    setUsername(response.data.username);
    return response.data.accessToken;
  }, [provider]);

  return { getToken, username };
}

function useProviders() {
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    adminApi.get('/api/admin/providers')
      .then((response) => setProviders(response.data.filter((p) => p.active)))
      .catch(() => message.error('Не удалось загрузить список провайдеров'));
  }, []);
  return providers;
}

function newKaspiTransactionId() {
  // 12 цифр, как у Kaspi; префикс 9 отличает тестовые сессии в списке транзакций.
  return Number(`9${String(Date.now()).slice(-11)}`);
}

function httpStatusColor(status) {
  if (status >= 200 && status < 300) return 'green';
  if (status >= 400 && status < 500) return 'orange';
  return 'red';
}

// Для ответов {result, comment, data} дополнительно показываем result - именно по нему решает клиент.
function resultTag(body) {
  if (body && typeof body === 'object' && typeof body.result === 'number') {
    return <Tag color={body.result === 0 ? 'green' : 'volcano'}>result {body.result}</Tag>;
  }
  if (body && typeof body === 'object' && typeof body.requestStatus === 'string') {
    return <Tag color={body.requestStatus === 'Success' ? 'green' : 'volcano'}>{body.requestStatus}</Tag>;
  }
  return null;
}

function ResponseView({ result }) {
  if (!result) {
    return <div className="kc-response kc-response--empty">Запрос ещё не отправлялся</div>;
  }
  return (
    <div className="kc-response">
      <Space wrap size={[6, 6]} className="kc-response__meta">
        <Tag color={httpStatusColor(result.status)}>HTTP {result.status || 'ошибка сети'}</Tag>
        {resultTag(result.body)}
        <Text type="secondary">{result.durationMs} мс · {result.at}</Text>
      </Space>
      <pre className="kc-response__body">
        {typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)}
      </pre>
    </div>
  );
}

// Одна операция интеграции в едином оформлении: номер, название, тип (цвет), метод и путь, кнопка, ответ.
function EndpointCard({ number, kind, title, description, buildRequest, values, getToken, methods, pollable }) {
  const [method, setMethod] = useState(methods ? methods[0] : null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const screens = Grid.useBreakpoint();
  const style = KINDS[kind];

  const request = buildRequest(values, method);
  // Тело сравнивается по содержимому: объект пересоздаётся на каждый рендер.
  const bodyJson = request.body !== undefined ? JSON.stringify(request.body) : undefined;

  const send = useCallback(async () => {
    setLoading(true);
    const started = performance.now();
    try {
      const headers = {};
      if (request.auth !== false) {
        headers.Authorization = `Bearer ${await getToken()}`;
      }
      if (bodyJson !== undefined) {
        headers['Content-Type'] = 'application/json';
      }
      const response = await integrationApi.request({
        method: request.method, url: request.path, data: bodyJson, headers,
      });
      setResult({
        status: response.status,
        body: response.data === '' ? '(пустое тело)' : response.data,
        durationMs: Math.round(performance.now() - started),
        at: new Date().toLocaleTimeString(),
      });
    } catch (error) {
      setResult({
        status: 0, body: String(error.message || error),
        durationMs: Math.round(performance.now() - started), at: new Date().toLocaleTimeString(),
      });
    } finally {
      setLoading(false);
    }
  }, [request.method, request.path, bodyJson, request.auth, getToken]);

  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(send, 2000);
    return () => clearInterval(timer);
  }, [polling, send]);

  return (
    <div className="kc-card" style={{ borderLeftColor: style.color }}>
      <div className="kc-card__header" style={{ background: style.background }}>
        <span className="kc-card__number" style={{ background: style.color }}>{number}</span>
        <span className="kc-card__title">{title}</span>
        <Tag className="kc-card__kind" color={style.color}>{style.label}</Tag>
      </div>
      <div className="kc-card__body">
        {description && <div className="kc-card__description">{description}</div>}
        <div className="kc-card__request">
          {methods ? (
            <Select size="small" value={method} onChange={setMethod} style={{ width: 84 }}
                    options={methods.map((m) => ({ value: m, label: m }))} />
          ) : (
            <Tag color={METHOD_COLORS[request.method]}>{request.method}</Tag>
          )}
          {request.auth === false && <Tag>без токена</Tag>}
          <Text code copyable className="kc-card__path">{request.path}</Text>
        </div>
        {bodyJson !== undefined && (
          <pre className="kc-card__payload">{JSON.stringify(request.body, null, 2)}</pre>
        )}
        <div className="kc-card__actions">
          <Button type="primary" loading={loading} onClick={send} block={!screens.sm}
                  danger={kind === 'stop'}
                  style={kind === 'start' ? { background: style.color, borderColor: style.color } : undefined}>
            Отправить
          </Button>
          {pollable && (
            <label className="kc-card__poll">
              <Switch size="small" checked={polling} onChange={setPolling} />
              <span>опрашивать каждые 2 с</span>
            </label>
          )}
        </div>
        <ResponseView result={result} />
      </div>
    </div>
  );
}

// Станции и коннекторы - из живого списка, чтобы нельзя было опечататься в номере станции.
function useChargePoints() {
  const [chargePoints, setChargePoints] = useState([]);
  const load = useCallback(async () => {
    const response = await integrationApi.get('/api/test/get-chargepoint-list');
    if (response.status === 200 && Array.isArray(response.data)) {
      setChargePoints(response.data);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return { chargePoints, reload: load };
}

// Общие параметры для всех операций страницы.
function SessionFields({ values, setValues, showSessionIndex, idLabel }) {
  const set = (key) => (value) => setValues((prev) => ({ ...prev, [key]: value }));
  const { chargePoints, reload } = useChargePoints();
  const stationOptions = [...new Set(chargePoints.map((c) => c.chargePointSerialNumber))]
    .map((serial) => ({ value: serial, label: serial }));
  const connectorOptions = chargePoints
    .filter((c) => c.chargePointSerialNumber === values.station)
    .map((c) => ({ value: c.connectorId, label: `${c.connectorId} · ${c.connectorStatus}` }));

  const loadSessionIndex = async () => {
    const response = await integrationApi.get(`/api/test/get-chargepoint/${values.station}/${values.connector}`);
    if (response.status === 200 && response.data && response.data.sessionIndex) {
      set('sessionIndex')(response.data.sessionIndex);
      message.success(`sessionIndex получен, статус коннектора: ${response.data.connectorStatus}`);
    } else {
      message.warning(`Не удалось получить sessionIndex (HTTP ${response.status})`);
    }
  };

  return (
    <div className="kc-params">
      <div className="kc-params__title">Параметры запросов</div>
      <Row gutter={[12, 10]}>
        <Col xs={12} sm={8} md={4}>
          <div className="kc-params__label">Станция</div>
          <Select value={values.station} options={stationOptions} style={{ width: '100%' }}
                  onChange={(station) => setValues((prev) => ({ ...prev, station, connector: 1, sessionIndex: '' }))} />
        </Col>
        <Col xs={12} sm={8} md={5}>
          <div className="kc-params__label">Коннектор · статус</div>
          <Select value={values.connector} options={connectorOptions} onChange={set('connector')} style={{ width: '100%' }} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <div className="kc-params__label">Сумма, тг</div>
          <InputNumber min={1} value={values.amount} onChange={set('amount')} style={{ width: '100%' }} />
        </Col>
        <Col xs={24} sm={16} md={7}>
          <div className="kc-params__label">{idLabel}</div>
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber value={values.kaspiId} onChange={set('kaspiId')} style={{ width: '100%' }} controls={false} />
            <Button onClick={() => set('kaspiId')(newKaspiTransactionId())}>Новый</Button>
          </Space.Compact>
        </Col>
        <Col xs={12} sm={8} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button onClick={reload} block>Обновить статусы</Button>
        </Col>
        {showSessionIndex && (
          <Col xs={24} md={12}>
            <div className="kc-params__label">sessionIndex (для старта v1)</div>
            <Space.Compact style={{ width: '100%' }}>
              <Input value={values.sessionIndex} onChange={(e) => set('sessionIndex')(e.target.value)} />
              <Button onClick={loadSessionIndex}>Взять со станции</Button>
            </Space.Compact>
          </Col>
        )}
      </Row>
    </div>
  );
}

function ConsoleHeader({ username, version, provider }) {
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={`Настоящие запросы ${version} к рабочему бэкенду`}
      description={
        <>
          Старт действительно запускает зарядку и создаёт оплату, остановка действительно списывает деньги.
          Запросы идут от вашей учётной записи{username ? <> (<Text code>{username}</Text>)</> : ''}
          {provider ? <> от имени провайдера <Text code>{provider}</Text></> : ''} с тем же заголовком
          <Text code>Authorization: Bearer …</Text>, что использует провайдер.
        </>
      }
    />
  );
}

function SectionTitle({ children }) {
  return <div className="kc-section-title">{children}</div>;
}

// Карточки раскладываются в сетку: одна колонка на телефоне, две на широком экране.
function CardGrid({ children }) {
  return (
    <Row gutter={[12, 12]}>
      {React.Children.map(children, (child) => <Col xs={24} xl={12}>{child}</Col>)}
    </Row>
  );
}

const initialValues = () => ({
  station: '103', connector: 1, amount: 1000, kaspiId: newKaspiTransactionId(), sessionIndex: '',
});

export function KaspiV1Tab() {
  const { getToken, username } = useConsoleToken(null);
  const [values, setValues] = useState(initialValues);
  const common = { values, getToken };

  return (
    <div className="kc">
      <ConsoleHeader username={username} version="Kaspi v1" />
      <SessionFields values={values} setValues={setValues} showSessionIndex idLabel="kaspiTransactionId" />

      <SectionTitle>Зарядка</SectionTitle>
      <CardGrid>
        <EndpointCard {...common} number={1} kind="start" title="Старт зарядки"
          description="Нужен sessionIndex - кнопка «Взять со станции» в параметрах."
          buildRequest={(v) => ({
            method: 'POST',
            path: `/api/kaspi/remote-start-transaction/${v.sessionIndex || '{sessionIndex}'}/${v.station}/${v.connector}/${v.amount}/${v.kaspiId}`,
          })} />
        <EndpointCard {...common} number={2} kind="read" title="Транзакция по kaspiTransactionId" pollable
          buildRequest={(v) => ({ method: 'GET', path: `/api/kaspi/get-remote-transaction-by-kaspitransactionid/${v.kaspiId}` })} />
        <EndpointCard {...common} number={3} kind="read" title="Статус зарядки {result, comment, data}" pollable
          buildRequest={(v) => ({ method: 'GET', path: `/api/kaspi/get-charging-status/${v.kaspiId}` })} />
        <EndpointCard {...common} number={4} kind="stop" title="Остановка зарядки" methods={['GET', 'POST']}
          description="Kaspi вызывает GET. Ожидание подтверждения станции - до 12 с."
          buildRequest={(v, method) => ({ method, path: `/api/kaspi/stop-transaction/${v.kaspiId}` })} />
      </CardGrid>

      <SectionTitle>Станции и справочные данные</SectionTitle>
      <CardGrid>
        <EndpointCard {...common} number={5} kind="info" title="Список станций" pollable
          buildRequest={() => ({ method: 'GET', path: '/api/test/get-chargepoint-list', auth: false })} />
        <EndpointCard {...common} number={6} kind="info" title="Станция" pollable
          buildRequest={(v) => ({ method: 'GET', path: `/api/test/get-chargepoint/${v.station}`, auth: false })} />
        <EndpointCard {...common} number={7} kind="info" title="Коннектор" pollable
          description="Старт возможен при connectorStatus = Preparing."
          buildRequest={(v) => ({ method: 'GET', path: `/api/test/get-chargepoint/${v.station}/${v.connector}`, auth: false })} />
        <EndpointCard {...common} number={8} kind="info" title="SoC по открытым транзакциям" pollable
          buildRequest={() => ({ method: 'GET', path: '/api/test/get-soc-by-transaction', auth: false })} />
        <EndpointCard {...common} number={9} kind="info" title="Расчёт кВт·ч (фиксированно 70 тг)"
          buildRequest={(v) => ({ method: 'GET', path: `/api/test/get-calc/${v.station}/${v.amount}`, auth: false })} />
      </CardGrid>
    </div>
  );
}

export function KaspiV2Tab() {
  const providers = useProviders();
  const [provider, setProvider] = useState('KASPI');
  const { getToken, username } = useConsoleToken(provider);
  const [values, setValues] = useState(initialValues);
  const common = { values, getToken };

  return (
    <div className="kc">
      <ConsoleHeader username={username} version="API v2" provider={provider} />
      <div className="kc-params" style={{ marginBottom: 12 }}>
        <div className="kc-params__label">Провайдер (от чьего имени запросы)</div>
        <Select value={provider} onChange={setProvider} style={{ width: 220, maxWidth: '100%' }}
                options={providers.map((p) => ({ value: p.code, label: `${p.code} · ${p.name || ''}` }))} />
      </div>
      <SessionFields values={values} setValues={setValues} idLabel="externalTransactionId" />

      <SectionTitle>Зарядка</SectionTitle>
      <CardGrid>
        <EndpointCard {...common} number={1} kind="start" title="Старт транзакции"
          description="Проверяет станцию, сумму и готовность коннектора до записи в БД."
          buildRequest={(v) => ({
            method: 'POST', path: '/api/v2/sessions',
            body: { stationSerialNumber: v.station, connectorId: v.connector, amount: v.amount, externalTransactionId: v.kaspiId },
          })} />
        <EndpointCard {...common} number={2} kind="read" title="Статус транзакции" pollable
          buildRequest={(v) => ({ method: 'GET', path: `/api/v2/sessions/${v.kaspiId}` })} />
        <EndpointCard {...common} number={3} kind="stop" title="Стоп транзакции"
          description="Ожидание подтверждения станции - до 12 с."
          buildRequest={(v) => ({ method: 'POST', path: `/api/v2/sessions/${v.kaspiId}/stop` })} />
      </CardGrid>

      <SectionTitle>Станции и справочные данные</SectionTitle>
      <CardGrid>
        <EndpointCard {...common} number={4} kind="info" title="Список станций" pollable
          buildRequest={() => ({ method: 'GET', path: '/api/v2/stations' })} />
        <EndpointCard {...common} number={5} kind="info" title="Станция" pollable
          buildRequest={(v) => ({ method: 'GET', path: `/api/v2/stations/${v.station}` })} />
        <EndpointCard {...common} number={6} kind="info" title="Коннектор" pollable
          description="readyToStart = true - можно запускать."
          buildRequest={(v) => ({ method: 'GET', path: `/api/v2/stations/${v.station}/connectors/${v.connector}` })} />
        <EndpointCard {...common} number={7} kind="info" title="Текущий тариф"
          buildRequest={() => ({ method: 'GET', path: '/api/v2/tariff' })} />
        <EndpointCard {...common} number={8} kind="info" title="Расчёт кВт·ч на сумму"
          buildRequest={(v) => ({ method: 'GET', path: `/api/v2/tariff/calculation?amount=${v.amount}` })} />
      </CardGrid>
    </div>
  );
}
